/**
 * Auth-Store (Zustand) — Token-Provider, User-Info, Rollen.
 * Herkunft: Copy-Fork aus rmnc-personal/frontend/src/store/auth.ts.
 *
 * Zwei Modi (VITE_AUTH_MODE):
 *  - "logto" (Default): Der AuthProvider registriert einen Token-Provider
 *    (Logto-SDK); der Axios-Interceptor holt darueber das Bearer-Token.
 *  - "dev": Kein Logto noetig. Der Interceptor sendet stattdessen den Header
 *    X-Dev-User mit einer JSON-Identitaet (konfigurierbar ueber
 *    localStorage-Key "devUser"), das Backend vertraut ihm nur im Dev-Betrieb.
 *
 * Merke (Coding-Standards): Rollen im Frontend nur zum Ein-/Ausblenden nutzen —
 * durchgesetzt wird immer serverseitig.
 */

import { create } from "zustand"
import type { DevUser } from "@/types"

// =============================================================================
// AUTH-MODUS + DEV-IDENTITAET
// =============================================================================

export const AUTH_MODE: "dev" | "logto" =
  import.meta.env.VITE_AUTH_MODE === "dev" ? "dev" : "logto"

const DEFAULT_DEV_USER: DevUser = {
  sub: "dev",
  name: "Dev Admin",
  roles: ["admin"],
}

/** Dev-Identitaet: localStorage "devUser" (JSON) mit Fallback auf Dev Admin */
export function getDevUser(): DevUser {
  try {
    const raw = localStorage.getItem("devUser")
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<DevUser>
      return {
        sub: parsed.sub ?? DEFAULT_DEV_USER.sub,
        name: parsed.name ?? DEFAULT_DEV_USER.name,
        roles: Array.isArray(parsed.roles) ? parsed.roles : DEFAULT_DEV_USER.roles,
      }
    }
  } catch {
    // Ungueltiges JSON -> Fallback
  }
  return DEFAULT_DEV_USER
}

// =============================================================================
// TOKEN-PROVIDER (wird vom AuthProvider registriert, ausserhalb von React nutzbar)
// =============================================================================

type TokenProvider = () => Promise<string | null>

let _tokenProvider: TokenProvider | null = null

export function setTokenProvider(provider: TokenProvider | null): void {
  _tokenProvider = provider
}

export async function getAccessToken(): Promise<string | null> {
  if (!_tokenProvider) return null
  try {
    return await _tokenProvider()
  } catch {
    return null
  }
}

// =============================================================================
// LOGOUT (Muster rmnc-personal: Handler vom AuthProvider registriert)
// =============================================================================

type AuthActionHandler = () => Promise<void> | void

let _logoutHandler: AuthActionHandler | null = null

/** Vom AuthProvider registriert: Logto-signOut mit Post-Logout-Redirect. */
export function setLogoutHandler(handler: AuthActionHandler | null): void {
  _logoutHandler = handler
}

/** Abmelden — im Dev-Modus nur Store leeren, im Logto-Modus signOut-Redirect. */
export async function performLogout(): Promise<void> {
  if (AUTH_MODE === "logto" && _logoutHandler) {
    await _logoutHandler()
    return
  }
  useAuthStore.getState().logout()
}

// =============================================================================
// STORE
// =============================================================================

export interface AuthUser {
  id: string
  name: string
  email: string | null
  roles: string[]
}

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean

  setUser: (user: AuthUser | null) => void
  setLoading: (loading: boolean) => void
  logout: () => void

  hasRole: (role: string) => boolean
  isAdmin: () => boolean
  isKundenadmin: () => boolean
  isReviewer: () => boolean
  /** Reviewer MIT Einschränkung (keine Schema-Historie, Admin nur Verbrauch). */
  isEingeschraenkt: () => boolean
  /** Voller Reviewer: darf Interna (Historie/KI-Konfig). Admin immer. */
  isReviewerVoll: () => boolean
}

/** Rollen der Kunden-Logto-Instanz (Umsetzungsplan §11.2) */
export const ROLLE_KUNDENADMIN = "tester:kundenadmin"
export const ROLLE_REVIEWER = "tester:reviewer"
export const ROLLE_EINGESCHRAENKT = "tester:eingeschraenkt"

export const useAuthStore = create<AuthState>()((set, get) => ({
  // Im Dev-Modus ist die Identitaet sofort da — kein Login-Roundtrip
  user:
    AUTH_MODE === "dev"
      ? (() => {
          const dev = getDevUser()
          return { id: dev.sub, name: dev.name, email: null, roles: dev.roles }
        })()
      : null,
  isAuthenticated: AUTH_MODE === "dev",
  isLoading: false,

  setUser: (user) => set({ user, isAuthenticated: user !== null }),
  setLoading: (isLoading) => set({ isLoading }),
  logout: () => set({ user: null, isAuthenticated: false, isLoading: false }),

  hasRole: (role) => {
    const { user } = get()
    return user?.roles.includes(role) ?? false
  },
  isAdmin: () => get().hasRole("admin"),
  isKundenadmin: () => get().hasRole(ROLLE_KUNDENADMIN) || get().isAdmin(),
  isReviewer: () => get().hasRole(ROLLE_REVIEWER) || get().isAdmin(),
  isEingeschraenkt: () => get().hasRole(ROLLE_EINGESCHRAENKT),
  isReviewerVoll: () => get().isAdmin() || (get().isReviewer() && !get().isEingeschraenkt()),
}))
